<template>
  <div class="base-config">
    <div class="page-header">
      <h2 class="page-title">基础设置</h2>
      <p class="page-desc">配置平台基础信息、分享设置、客服配置、协议内容等</p>
    </div>

    <div class="config-container">
      <el-tabs v-model="activeTab" type="border-card">
        <!-- 平台信息 -->
        <el-tab-pane label="平台信息" name="platform">
          <el-form :model="form" ref="formRef" label-width="140px">
            <el-form-item label="平台状态">
              <el-switch v-model="form.platform_status" :active-value="1" :inactive-value="0" active-text="开启"
                inactive-text="关闭" />
              <div class="form-tip">关闭后用户端将无法访问</div>
            </el-form-item>

            <el-form-item label="静默登录">
              <el-switch v-model="form.silent_login" :active-value="1" :inactive-value="0" active-text="开启"
                inactive-text="关闭" />
              <div class="form-tip">开启后用户无需授权即可自动登录，建议关闭时配合强制获取手机号使用</div>
            </el-form-item>

            <el-form-item label="强制获取手机号">
              <el-switch v-model="form.force_get_mobile" :active-value="1" :inactive-value="0" active-text="开启"
                inactive-text="关闭" />
              <div class="form-tip">开启后用户必须授权手机号才能使用小程序，提升用户真实性</div>
            </el-form-item>

            <el-form-item label="名称前缀">
              <el-input v-model="form.name_prefix" placeholder="请输入小程序用户名称前缀" maxlength="30" style="width: 300px" />
              <div class="form-tip">用户名称将显示为"前缀+昵称"，如设置前缀为"视频号"，用户昵称为"张三"，则显示"视频号·张三"</div>
            </el-form-item>

            <el-form-item label="平台LOGO">
              <div class="upload-wrapper">
                <el-upload :file-list="logoFileList" action="#" :limit="1" accept="image/*" :auto-upload="false"
                  :on-change="handleLogoChange" :on-remove="() => form.platform_logo = ''" list-type="picture-card">
                  <el-icon>
                    <Plus />
                  </el-icon>
                </el-upload>
                <div class="upload-tip">建议尺寸200x200px，支持JPG/PNG</div>
              </div>
            </el-form-item>

            <el-form-item label="平台名称">
              <el-input v-model="form.platform_name" placeholder="请输入平台名称" maxlength="50" />
            </el-form-item>

            <el-form-item label="平台简介">
              <el-input v-model="form.platform_desc" type="textarea" :rows="3" placeholder="请输入平台简介" maxlength="200"
                show-word-limit />
            </el-form-item>

            <!-- 两级下拉选择 -->
            <el-form-item label="所属行业">
              <div class="industry-select-wrapper">
                <el-select v-model="selectedFirstIndustry" placeholder="请选择一级行业"
                  style="width: 250px; margin-right: 12px;" clearable @change="onFirstIndustryChange">
                  <el-option v-for="item in firstIndustryOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>

                <el-select v-model="selectedSecondIndustry" placeholder="请选择二级行业" style="width: 250px"
                  :disabled="!selectedFirstIndustry" clearable @change="onSecondIndustryChange">
                  <el-option v-for="item in secondIndustryOptions" :key="item.value" :label="item.label"
                    :value="item.value" />
                </el-select>
              </div>
              <div class="form-tip">请先选择一级行业，再选择二级行业</div>
            </el-form-item>

            <!-- 服务类型提示（仅展示，不保存） -->
            <el-form-item v-if="serviceTypeOptions.length > 0" label="支持服务">
              <div class="service-tags">
                <el-tag v-for="tag in serviceTypeOptions" :key="tag.serviceTypeId" class="service-tag" type="info">
                  {{ tag.serviceTypeName }}
                </el-tag>
              </div>
              <div class="form-tip">该行业支持以上服务类型，可在小程序端展示</div>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 分享设置 -->
        <el-tab-pane label="分享设置" name="share">
          <el-form :model="form" label-width="120px">
            <el-form-item label="分享标题">
              <el-input v-model="form.share_title" placeholder="请输入分享标题" maxlength="50" show-word-limit />
              <div class="form-tip">用户分享时显示的标题</div>
            </el-form-item>

            <el-form-item label="分享简介">
              <el-input v-model="form.share_desc" type="textarea" :rows="3" placeholder="请输入分享简介" maxlength="200"
                show-word-limit />
              <div class="form-tip">用户分享时显示的描述文字</div>
            </el-form-item>

            <el-form-item label="分享图标">
              <div class="upload-wrapper">
                <el-upload :file-list="shareIconFileList" action="#" :limit="1" accept="image/*" :auto-upload="false"
                  :on-change="handleShareIconChange" :on-remove="() => form.share_icon = ''" list-type="picture-card">
                  <el-icon>
                    <Plus />
                  </el-icon>
                </el-upload>
                <div class="upload-tip">建议尺寸200x200px，支持JPG/PNG</div>
              </div>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 客服配置 -->
        <el-tab-pane label="客服配置" name="customer">
          <el-form :model="form" label-width="140px">
            <el-form-item label="客服类型">
              <el-radio-group v-model="form.customer_type" @change="onCustomerTypeChange">
                <el-radio value="none">无客服</el-radio>
                <el-radio value="wxwork">企业微信客服</el-radio>
                <el-radio value="wxgzh">微信公众号客服</el-radio>
                <el-radio value="normal">普通客服</el-radio>
              </el-radio-group>
            </el-form-item>

            <template v-if="form.customer_type === 'wxwork'">
              <el-form-item label="企业ID">
                <el-input v-model="form.wxwork_corp_id" placeholder="请输入企业微信企业ID" />
              </el-form-item>
              <el-form-item label="客服AgentId">
                <el-input v-model="form.wxwork_agent_id" placeholder="请输入客服AgentId" />
              </el-form-item>
              <el-form-item label="客服Secret">
                <el-input v-model="form.wxwork_secret" type="password" placeholder="请输入客服Secret" show-password />
              </el-form-item>
            </template>

            <template v-if="form.customer_type === 'wxgzh'">
              <el-form-item label="公众号AppID">
                <el-input v-model="form.wxgzh_appid" placeholder="请输入微信公众号AppID" />
              </el-form-item>
              <el-form-item label="公众号Secret">
                <el-input v-model="form.wxgzh_secret" type="password" placeholder="请输入微信公众号Secret" show-password />
              </el-form-item>
            </template>

            <template v-if="form.customer_type === 'normal'">
              <el-form-item label="客服二维码">
                <div class="upload-wrapper">
                  <el-upload :file-list="normalQrcodeFileList" action="#" :limit="1" accept="image/*"
                    :auto-upload="false" :on-change="handleNormalQrcodeChange"
                    :on-remove="() => form.normal_qrcode = ''" list-type="picture-card">
                    <el-icon>
                      <Plus />
                    </el-icon>
                  </el-upload>
                  <div class="upload-tip">请上传客服微信二维码</div>
                </div>
              </el-form-item>
              <el-form-item label="客服手机号">
                <el-input v-model="form.normal_phone" placeholder="请输入客服手机号" />
              </el-form-item>
            </template>
          </el-form>
        </el-tab-pane>

        <!-- 服务协议 -->
        <el-tab-pane label="服务协议" name="service">
          <el-form :model="form" label-width="120px">
            <el-form-item label="协议名称">
              <el-input v-model="form.service_agreement_name" placeholder="请输入服务协议名称" maxlength="50" />
            </el-form-item>
            <el-form-item label="协议内容">
              <div class="editor-wrapper">
                <QuillEditor ref="serviceQuillRef" v-model:content="form.service_agreement_content" contentType="html"
                  :options="editorOptions" style="height: 400px" />
              </div>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 隐私协议 -->
        <el-tab-pane label="隐私协议" name="privacy">
          <el-form :model="form" label-width="120px">
            <el-form-item label="协议名称">
              <el-input v-model="form.privacy_agreement_name" placeholder="请输入隐私协议名称" maxlength="50" />
            </el-form-item>
            <el-form-item label="协议内容">
              <div class="editor-wrapper">
                <QuillEditor ref="privacyQuillRef" v-model:content="form.privacy_agreement_content" contentType="html"
                  :options="editorOptions" style="height: 400px" />
              </div>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 备案管理 -->
        <el-tab-pane label="备案管理" name="record">
          <el-form :model="form" label-width="140px">
            <el-form-item label="域名备案号">
              <el-input v-model="form.record_domain" placeholder="请输入域名备案号，如：陕ICP备2024057741号-4" maxlength="100" />
              <div class="form-tip">工信部ICP备案号，显示在网站底部</div>
            </el-form-item>

            <el-form-item label="公安局备案号">
              <el-input v-model="form.record_public_security" placeholder="请输入公安局备案号，如：沪公网安备31010502000000号"
                maxlength="100" />
              <div class="form-tip">公安网安备案号，显示在网站底部</div>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>

      <div class="save-footer">
        <el-button type="primary" @click="saveConfig" :loading="submitting" size="large" class="save-btn">
          <el-icon>
            <Check />
          </el-icon>保存所有配置
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { Plus, Check } from '@element-plus/icons-vue'

export default {
  name: 'BaseConfig',
  components: { QuillEditor, Plus, Check },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const storeId = ref(userInfo.id || 0)
    const agentId = ref(userInfo.agent_id || 0)

    const activeTab = ref('platform')
    const formRef = ref(null)
    const serviceQuillRef = ref(null)
    const privacyQuillRef = ref(null)
    const submitting = ref(false)

    const logoFileList = ref([])
    const shareIconFileList = ref([])
    const normalQrcodeFileList = ref([])

    // 行业选择相关
    const firstIndustryOptions = ref([])      // 一级行业选项
    const secondIndustryOptions = ref([])     // 二级行业选项
    const selectedFirstIndustry = ref(null)   // 选中的一级行业ID
    const selectedSecondIndustry = ref(null)  // 选中的二级行业ID
    const serviceTypeOptions = ref([])        // 服务类型提示
    // 行业映射表（用于快速查找服务类型）
    const industryMap = ref(new Map())

    const form = reactive({
      platform_status: 1,
      silent_login: 0,                       // 静默登录
      force_get_mobile: 0,                   // 强制获取手机号
      name_prefix: '',                       // 名称前缀
      platform_logo: '',
      platform_name: '',
      platform_desc: '',
      industry: '',
      industry_custom: '',
      industry_id: '',
      industry_name: '',
      share_title: '',
      share_desc: '',
      share_icon: '',
      customer_type: 'none',
      wxwork_corp_id: '',
      wxwork_agent_id: '',
      wxwork_secret: '',
      wxgzh_appid: '',
      wxgzh_secret: '',
      normal_qrcode: '',
      normal_phone: '',
      service_agreement_name: '',
      service_agreement_content: '',
      privacy_agreement_name: '',
      privacy_agreement_content: '',
      record_domain: '',
      record_public_security: ''
    })

    const editorOptions = {
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ direction: 'rtl' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ['clean'],
            ['link', 'image', 'video']
          ],
          handlers: {
            image: function () {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'image/*')
              input.click()
              input.onchange = async () => {
                const file = input.files[0]
                if (!file) return
                const loading = ElLoading.service({ text: '上传图片中...', background: 'rgba(0, 0, 0, 0.7)' })
                try {
                  const range = this.quill.getSelection(true)
                  const url = await uploadImage(file)
                  this.quill.insertEmbed(range.index, 'image', url)
                  this.quill.setSelection(range.index + 1)
                  ElMessage.success('图片上传成功')
                } catch (error) {
                  console.error('上传失败:', error)
                  ElMessage.error(error.message || '图片上传失败')
                } finally {
                  loading.close()
                }
              }
            }
          }
        }
      },
      placeholder: '请输入协议内容...',
      theme: 'snow'
    }

    const uploadImage = async (file) => {
      if (!file.type.startsWith('image/')) {
        throw new Error('只能上传图片文件')
      }
      if (file.size / 1024 / 1024 > 5) {
        throw new Error('图片大小不能超过 5MB')
      }
      const signature = await getUploadSignature('image')
      const ossRes = await uploadToOSS(file, signature)
      return ossRes.url
    }

    const uploadSingleImage = async (file, field) => {
      if (!file.type.startsWith('image/')) {
        ElMessage.error('只能上传图片文件')
        return null
      }
      if (file.size / 1024 / 1024 > 5) {
        ElMessage.error('图片大小不能超过 5MB')
        return null
      }
      try {
        const signature = await getUploadSignature('image')
        const ossRes = await uploadToOSS(file, signature)
        form[field] = ossRes.url
        return ossRes.url
      } catch (error) {
        console.error('上传失败:', error)
        ElMessage.error('上传失败')
        return null
      }
    }

    const handleLogoChange = async (file) => {
      if (!file.raw) return
      const url = await uploadSingleImage(file.raw, 'platform_logo')
      if (url) {
        logoFileList.value = [{ uid: file.uid, name: file.name, url }]
        ElMessage.success('上传成功')
      }
    }

    const handleShareIconChange = async (file) => {
      if (!file.raw) return
      const url = await uploadSingleImage(file.raw, 'share_icon')
      if (url) {
        shareIconFileList.value = [{ uid: file.uid, name: file.name, url }]
        ElMessage.success('上传成功')
      }
    }

    const handleNormalQrcodeChange = async (file) => {
      if (!file.raw) return
      const url = await uploadSingleImage(file.raw, 'normal_qrcode')
      if (url) {
        normalQrcodeFileList.value = [{ uid: file.uid, name: file.name, url }]
        ElMessage.success('上传成功')
      }
    }

    // 加载行业数据
    const loadIndustryData = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/industry/api_industry.php`)

        // 构建一级行业选项（从 cascadeData 中提取）
        if (response.data && response.data.cascadeData) {
          firstIndustryOptions.value = response.data.cascadeData.map(item => ({
            value: item.value,
            label: item.label
          }))
        }

        // 构建行业映射表（用于服务类型查找）
        if (response.data && response.data.industryOptions) {
          const map = new Map()
          response.data.industryOptions.forEach(item => {
            map.set(item.industryId, item)
          })
          industryMap.value = map
          console.log('行业映射表构建完成，共', map.size, '个行业')
        }
      } catch (error) {
        console.error('加载行业数据失败:', error)
        ElMessage.error('加载行业数据失败')
      }
    }

    // 一级行业变化时，加载对应的二级行业选项
    const onFirstIndustryChange = (value) => {
      // 清空二级行业选择
      selectedSecondIndustry.value = null
      secondIndustryOptions.value = []
      // 清空服务类型提示
      serviceTypeOptions.value = []
      // 清空表单中的行业信息
      form.industry = ''
      form.industry_id = ''
      form.industry_name = ''

      if (value) {
        // 从 industryMap 中筛选属于当前一级行业的二级行业
        const secondIndustries = []
        industryMap.value.forEach((industry, key) => {
          if (industry.parentIndustryId === value) {
            secondIndustries.push({
              value: key,
              label: industry.industryLevelName
            })
          }
        })
        secondIndustryOptions.value = secondIndustries
      }
    }

    // 二级行业变化时，保存行业信息并显示服务类型
    const onSecondIndustryChange = (value) => {
      if (value) {
        const industryInfo = industryMap.value.get(value)
        if (industryInfo) {
          // 获取一级行业名称
          const firstIndustry = firstIndustryOptions.value.find(item => item.value === industryInfo.parentIndustryId)
          form.industry = firstIndustry ? firstIndustry.label : ''
          form.industry_id = String(value)
          form.industry_name = industryInfo.industryLevelName
          serviceTypeOptions.value = industryInfo.serviceTypeOptions || []
          console.log('选择的行业信息:', {
            industry: form.industry,
            industry_id: form.industry_id,
            industry_name: form.industry_name,
            serviceTypeOptions: serviceTypeOptions.value
          })
        }
      } else {
        // 清空行业信息
        form.industry = ''
        form.industry_id = ''
        form.industry_name = ''
        serviceTypeOptions.value = []
      }
    }

    // 根据 industry_id 回显行业选择
    const setIndustrySelection = (industryId) => {
      if (!industryId) {
        selectedFirstIndustry.value = null
        selectedSecondIndustry.value = null
        secondIndustryOptions.value = []
        return
      }

      const targetId = typeof industryId === 'string' ? parseInt(industryId) : industryId
      const industryInfo = industryMap.value.get(targetId)
      if (industryInfo) {
        // 设置一级行业
        selectedFirstIndustry.value = industryInfo.parentIndustryId
        // 加载二级行业选项
        const secondIndustries = []
        industryMap.value.forEach((value, key) => {
          if (value.parentIndustryId === industryInfo.parentIndustryId) {
            secondIndustries.push({
              value: key,
              label: value.industryLevelName
            })
          }
        })
        secondIndustryOptions.value = secondIndustries
        // 设置二级行业
        selectedSecondIndustry.value = targetId
        // 设置表单数据
        const firstIndustry = firstIndustryOptions.value.find(item => item.value === industryInfo.parentIndustryId)
        form.industry = firstIndustry ? firstIndustry.label : ''
        form.industry_id = String(targetId)
        form.industry_name = industryInfo.industryLevelName
        serviceTypeOptions.value = industryInfo.serviceTypeOptions || []
      }
    }

    const onCustomerTypeChange = () => {
      if (form.customer_type !== 'wxwork') {
        form.wxwork_corp_id = ''
        form.wxwork_agent_id = ''
        form.wxwork_secret = ''
      }
      if (form.customer_type !== 'wxgzh') {
        form.wxgzh_appid = ''
        form.wxgzh_secret = ''
      }
      if (form.customer_type !== 'normal') {
        form.normal_qrcode = ''
        form.normal_phone = ''
      }
    }

    const fetchConfig = async () => {
      const loading = ElLoading.service({ text: '加载中...' })
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/base_config_api.php`, {
          params: { store_id: storeId.value, agent_id: agentId.value }
        })
        if (res.data.code === 200 && res.data.data) {
          Object.assign(form, res.data.data)

          // 恢复图片预览
          if (form.platform_logo) {
            logoFileList.value = [{ uid: Date.now(), name: 'logo', url: form.platform_logo }]
          }
          if (form.share_icon) {
            shareIconFileList.value = [{ uid: Date.now(), name: 'share_icon', url: form.share_icon }]
          }
          if (form.normal_qrcode) {
            normalQrcodeFileList.value = [{ uid: Date.now(), name: 'qrcode', url: form.normal_qrcode }]
          }

          // 回显行业选择
          if (form.industry_id) {
            setIndustrySelection(form.industry_id)
          }
        }
      } catch (err) {
        console.error(err)
        ElMessage.error('获取配置失败')
      } finally {
        loading.close()
      }
    }

    const saveConfig = async () => {
      submitting.value = true
      const loading = ElLoading.service({ text: '保存中...' })

      try {
        const payload = {
          store_id: storeId.value,
          agent_id: agentId.value,
          platform_status: form.platform_status,
          silent_login: form.silent_login,
          force_get_mobile: form.force_get_mobile,
          name_prefix: form.name_prefix,
          platform_logo: form.platform_logo,
          platform_name: form.platform_name,
          platform_desc: form.platform_desc,
          industry: form.industry,
          industry_custom: form.industry_custom,
          industry_id: form.industry_id,
          industry_name: form.industry_name,
          share_title: form.share_title,
          share_desc: form.share_desc,
          share_icon: form.share_icon,
          customer_type: form.customer_type,
          wxwork_corp_id: form.wxwork_corp_id,
          wxwork_agent_id: form.wxwork_agent_id,
          wxwork_secret: form.wxwork_secret,
          wxgzh_appid: form.wxgzh_appid,
          wxgzh_secret: form.wxgzh_secret,
          normal_qrcode: form.normal_qrcode,
          normal_phone: form.normal_phone,
          service_agreement_name: form.service_agreement_name,
          service_agreement_content: form.service_agreement_content,
          privacy_agreement_name: form.privacy_agreement_name,
          privacy_agreement_content: form.privacy_agreement_content,
          record_domain: form.record_domain,
          record_public_security: form.record_public_security
        }

        const res = await axios.post(`${config.BASE_URL}/shop/api/base_config_api.php`, payload)

        if (res.data.code === 200) {
          ElMessage.success('保存成功')
        } else {
          ElMessage.error(res.data.message || '保存失败')
        }
      } catch (err) {
        console.error(err)
        ElMessage.error('保存失败')
      } finally {
        submitting.value = false
        loading.close()
      }
    }

    onMounted(async () => {
      await loadIndustryData()
      fetchConfig()
    })

    return {
      activeTab,
      form,
      formRef,
      serviceQuillRef,
      privacyQuillRef,
      submitting,
      logoFileList,
      shareIconFileList,
      normalQrcodeFileList,
      editorOptions,
      firstIndustryOptions,
      secondIndustryOptions,
      selectedFirstIndustry,
      selectedSecondIndustry,
      serviceTypeOptions,
      handleLogoChange,
      handleShareIconChange,
      handleNormalQrcodeChange,
      onFirstIndustryChange,
      onSecondIndustryChange,
      onCustomerTypeChange,
      saveConfig
    }
  }
}
</script>

<style scoped lang="scss">
.base-config {
  .page-header {
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .config-container {
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
  }

  .form-tip {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
    margin-left: 5px;
    line-height: 1.4;
  }

  .upload-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 8px;
  }

  .editor-wrapper {
    width: 100%;

    :deep(.ql-toolbar) {
      border-radius: 8px 8px 0 0;
    }

    :deep(.ql-container) {
      border-radius: 0 0 8px 8px;
      min-height: 300px;
    }
  }

  .industry-select-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .service-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .service-tag {
      font-size: 13px;
    }
  }

  .save-footer {
    padding: 20px;
    text-align: center;
    border-top: 1px solid #f0f0f0;
  }

  .save-btn {
    padding: 10px 32px;
    font-size: 16px;
    font-weight: 500;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    border: none !important;
  }
}
</style>